import { Component } from '@theme/component';

class LevayoSearchMenu extends Component {
  requiredRefs = ['panel', 'discovery', 'results', 'status', 'allResults'];
  #controller;
  #fetch;
  #timer;
  #input;
  #cache = new Map();
  #ignoreFocus = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  #bind() {
    this.#cancel();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#input = document.getElementById(this.dataset.inputId);
    if (!(this.#input instanceof HTMLInputElement)) return;
    this.#input.setAttribute('aria-controls', this.refs.panel.id);
    this.#input.setAttribute('aria-haspopup', 'dialog');
    this.#input.setAttribute('aria-expanded', String(this.#isOpen()));
    this.#input.setAttribute('autocomplete', 'off');
    this.#input.addEventListener('focus', () => { if (!this.#ignoreFocus && document.activeElement === this.#input) this.open(); }, { signal });
    this.#input.addEventListener('click', () => this.open(), { signal });
    this.#input.addEventListener('input', () => { if (document.activeElement !== this.#input) return; if (!this.#isOpen()) this.open(); else this.#queueSearch(); }, { signal });
    this.#input.addEventListener('keydown', (event) => this.#handleKey(event), { signal });
    this.refs.panel.addEventListener('keydown', (event) => this.#handleKey(event), { signal });
    const leave = (event) => {
      if (event.relatedTarget && event.relatedTarget !== this.#input && !this.refs.panel.contains(event.relatedTarget)) this.close();
    };
    this.#input.addEventListener('focusout', leave, { signal });
    this.refs.panel.addEventListener('focusout', leave, { signal });
    this.refs.panel.addEventListener('toggle', (event) => {
      this.#input.setAttribute('aria-expanded', String(event.newState === 'open'));
      if (event.newState === 'closed') { this.refs.panel.hidden = true; this.#cancel(); }
    }, { signal });
    this.refs.panel.addEventListener('click', (event) => { if (event.target.closest('a')) this.close(); }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.refs.panel.contains(event.target) && event.target !== this.#input && !this.#input.closest('form, .lvc-search')?.contains(event.target)) this.close();
    }, { signal });
    document.addEventListener('scroll', () => { if (this.#isOpen()) this.#position(); }, { signal, passive: true, capture: true });
    window.addEventListener('resize', () => { if (this.#isOpen()) this.#position(); }, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', () => { if (this.#isOpen()) this.#position(); }, { signal, passive: true });
    window.visualViewport?.addEventListener('scroll', () => { if (this.#isOpen()) this.#position(); }, { signal, passive: true });
    this.#input.closest('form')?.addEventListener('submit', () => this.close(), { signal });
  }

  disconnectedCallback() {
    this.close();
    this.#controller?.abort();
    this.#cancel();
    super.disconnectedCallback();
  }

  #isOpen() { return !this.refs.panel.hidden && this.refs.panel.matches(':popover-open'); }

  open() {
    if (!this.#input || !this.refs.panel.showPopover) return;
    if (!this.#position()) return;
    this.refs.panel.hidden = false;
    if (!this.#isOpen()) this.refs.panel.showPopover();
    this.#position();
    this.#input.setAttribute('aria-expanded', 'true');
    this.#queueSearch();
  }

  close() {
    if (!this.refs.panel) return;
    if (this.#isOpen()) this.refs.panel.hidePopover();
    this.refs.panel.hidden = true;
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#cancel();
  }

  closeFromButton() {
    this.close();
    this.#ignoreFocus = true;
    this.#input?.focus({ preventScroll: true });
    this.#ignoreFocus = false;
  }

  #position() {
    const rect = (this.#input.closest('form, .lvc-search') || this.#input).getBoundingClientRect();
    const view = window.visualViewport;
    const viewTop = view?.offsetTop || 0;
    const bottom = (view?.height || innerHeight) + viewTop;
    if (!rect.width || !rect.height || rect.bottom < viewTop || rect.top > bottom) { this.close(); return false; }
    const width = Math.min(Math.max(rect.width, innerWidth <= 749 ? innerWidth - 24 : 660), innerWidth - 24);
    const left = Math.max(12, Math.min(rect.left, innerWidth - width - 12));
    const panel = this.refs.panel;
    const below = bottom - rect.bottom - 22;
    const above = rect.top - viewTop - 22;
    const placeAbove = below < 180 && above > below && above > 120;
    const height = Math.max(72, Math.min(placeAbove ? above : below, 560));
    const contentHeight = Math.min(height, panel.scrollHeight ? panel.scrollHeight + 2 : height);
    const top = placeAbove ? rect.top - contentHeight - 10 : Math.max(viewTop + 8, rect.bottom + 10);
    panel.style.setProperty('--lvsm-width', `${width}px`);
    panel.style.setProperty('--lvsm-left', `${left}px`);
    panel.style.setProperty('--lvsm-top', `${top}px`);
    panel.style.setProperty('--lvsm-height', `${height}px`);
    return true;
  }

  #cancel() { clearTimeout(this.#timer); this.#fetch?.abort(); }

  #queueSearch() {
    this.#cancel();
    const query = this.#input.value.trim();
    const searching = query.length > 0;
    this.refs.panel.toggleAttribute('data-searching', searching);
    this.refs.discovery.hidden = searching;
    this.refs.results.hidden = !searching;
    this.refs.allResults.hidden = !searching;
    this.refs.status.textContent = '';
    this.refs.status.classList.remove('visually-hidden');
    if (!searching) { this.refs.results.replaceChildren(); this.refs.results.removeAttribute('aria-busy'); return; }
    const all = new URL(this.dataset.searchUrl, location.origin);
    all.searchParams.set('q', query);
    all.searchParams.set('type', 'product');
    all.searchParams.set('options[prefix]', 'last');
    this.refs.allResults.href = all.href;
    this.refs.results.replaceChildren();
    this.refs.status.textContent = this.dataset.loading;
    this.refs.results.setAttribute('aria-busy', 'true');
    this.#timer = setTimeout(() => this.#search(query), 180);
  }

  async #search(query) {
    const controller = new AbortController();
    this.#fetch = controller;
    try {
      let markup = this.#cache.get(query);
      if (!markup) {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'levayo-search-results');
        url.searchParams.set('resources[type]', 'product,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search response unavailable');
        markup = await response.text();
      }
      if (controller.signal.aborted || query !== this.#input.value.trim() || !this.#isOpen()) return;
      const fragment = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-levayo-search-results]');
      if (!fragment) throw new Error('Search results unavailable');
      if (this.#cache.size >= 20) this.#cache.delete(this.#cache.keys().next().value);
      this.#cache.set(query, markup);
      this.refs.results.replaceChildren(fragment);
      this.refs.status.textContent = fragment.querySelector('[data-result-announcement]')?.textContent.trim() || '';
      this.refs.status.classList.add('visually-hidden');
    } catch (error) {
      if (controller.signal.aborted || query !== this.#input.value.trim()) return;
      this.refs.status.classList.remove('visually-hidden');
      this.refs.status.textContent = this.dataset.error;
    } finally {
      if (this.#fetch === controller) this.refs.results.removeAttribute('aria-busy');
    }
  }

  #handleKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeFromButton();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    this.#ignoreFocus = true;
    if (!this.#isOpen()) this.open();
    const links = Array.from(this.refs.panel.querySelectorAll('[data-search-link]')).filter(link => link.getClientRects().length > 0);
    if (!links.length) { this.#ignoreFocus = false; return; }
    const index = links.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    event.preventDefault();
    if (next < 0) this.#input.focus({ preventScroll: true });
    else {
      const link = links[Math.min(next, links.length - 1)];
      link.focus({ preventScroll: true });
      link.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
    this.#ignoreFocus = false;
  }
}

if (!customElements.get('levayo-search-menu')) customElements.define('levayo-search-menu', LevayoSearchMenu);
